import { AlertCircle, AlertTriangle, CheckCircle2, Info } from "lucide-react";
import cn from "./cn";

// DESIGN.md › Colors › How status is shown. The word always carries the
// meaning; colour and icon only reinforce it.
const TEXT = {
  neutral: "text-foreground",
  success: "text-success",
  warning: "text-warning",
  danger: "text-danger",
};

const ICONS = {
  neutral: Info,
  success: CheckCircle2,
  warning: AlertTriangle,
  danger: AlertCircle,
};

/**
 * Status — the default: a plain status word. Pass `icon` for exceptions that
 * must be found while scanning a list ("Needs review").
 */
export function Status({ tone = "neutral", icon = false, className, children }) {
  const Icon = ICONS[tone];
  return (
    <span className={cn("inline-flex items-center gap-1.5", TEXT[tone], className)}>
      {icon ? <Icon aria-hidden="true" className="h-3.5 w-3.5 shrink-0" /> : null}
      <span>{children}</span>
    </span>
  );
}

const BADGE = {
  neutral: "bg-surface-sunken text-secondary",
  success: "bg-success-subtle text-success",
  warning: "bg-warning-subtle text-warning",
  danger: "bg-danger-subtle text-danger",
};

/**
 * Badge — only when a status must stand out in a dense list (priority,
 * overdue, at risk). At most one per row.
 */
export function Badge({ tone = "neutral", className, children }) {
  return (
    <span className={cn("inline-flex items-center rounded-full px-2 py-0.5 text-meta font-medium", BADGE[tone], className)}>
      {children}
    </span>
  );
}

/** Small numeric counter (nav queues, unread). The one place 11 px is allowed. */
export function Counter({ tone = "neutral", className, children, ...props }) {
  return (
    <span
      className={cn(
        "inline-flex h-[18px] min-w-[18px] shrink-0 items-center justify-center rounded-full px-1.5 text-counter tabular-nums",
        tone === "accent" ? "bg-accent text-on-accent" : "bg-surface-sunken text-secondary",
        className
      )}
      {...props}
    >
      {children}
    </span>
  );
}
